import { useEffect, useState, type FormEvent } from "react";

import DownloadIcon from "@mui/icons-material/Download";
import LockResetIcon from "@mui/icons-material/LockReset";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { changePassword, downloadMyCv, getProfile, updateProfile } from "@/api/profile";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { IcsSection } from "@/components/IcsSection";
import { MfaPanel } from "@/components/MfaPanel";
import { MyUnavailabilitiesPanel } from "@/components/MyUnavailabilitiesPanel";
import { CoordinatorBackupPanel } from "@/components/CoordinatorBackupPanel";
import { NotificationPreferencesPanel } from "@/components/NotificationPreferencesPanel";
import { PageHeader } from "@/components/PageHeader";
import { PhotoUploader } from "@/components/PhotoUploader";
import { PushNotificationsPanel } from "@/components/PushNotificationsPanel";
import { SessionsPanel } from "@/components/SessionsPanel";
import { SsoLinkPanel } from "@/components/SsoLinkPanel";
import { useAuthStore } from "@/store/authStore";
import { useUIStore } from "@/store/uiStore";

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const setThemeMode = useUIStore((s) => s.setThemeMode);
  const refreshMe = useAuthStore((s) => s.loadMe);

  const profile = useQuery({
    queryKey: ["me-profile"],
    queryFn: () => getProfile(),
  });

  // Identity form
  const [fullName, setFullName] = useState("");
  const [locale, setLocale] = useState("fr");
  const [themePref, setThemePref] = useState("auto");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);
  const [languagesInput, setLanguagesInput] = useState("");
  const [identitySaved, setIdentitySaved] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);

  useEffect(() => {
    if (profile.data) {
      setFullName(profile.data.full_name ?? "");
      setLocale(profile.data.locale);
      setThemePref(profile.data.theme_pref);
      setPhone(profile.data.practitioner?.phone ?? "");
      setBio(profile.data.practitioner?.bio ?? "");
      const langs = profile.data.practitioner?.languages ?? [];
      setLanguages(langs);
      setLanguagesInput(langs.join(", "));
    }
  }, [profile.data]);

  const identityMutation = useMutation({
    mutationFn: () =>
      updateProfile({
        full_name: fullName || null,
        locale,
        theme_pref: themePref,
        practitioner_phone: profile.data?.practitioner ? phone || null : undefined,
        practitioner_bio: profile.data?.practitioner ? bio || null : undefined,
        practitioner_languages: profile.data?.practitioner ? languages : undefined,
      }),
    onSuccess: () => {
      setIdentitySaved(true);
      setIdentityError(null);
      qc.invalidateQueries({ queryKey: ["me-profile"] });
      void refreshMe();
      // Apply locale immediately
      if (locale && i18n.language !== locale) void i18n.changeLanguage(locale);
      // Apply theme to UI store
      if (themePref === "light" || themePref === "dark" || themePref === "auto") {
        setThemeMode(themePref);
      }
      setTimeout(() => setIdentitySaved(false), 2500);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setIdentityError(typeof detail === "string" ? detail : t("profile.saveError"));
    },
  });

  // Password form
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdSaved, setPwdSaved] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);

  const pwdMutation = useMutation({
    mutationFn: () => changePassword(currentPwd, newPwd),
    onSuccess: () => {
      setPwdSaved(true);
      setPwdError(null);
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setTimeout(() => setPwdSaved(false), 2500);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      if (detail === "wrong_current_password") setPwdError(t("profile.wrongCurrentPassword"));
      else if (detail === "new_password_must_differ") setPwdError(t("profile.newPasswordMustDiffer"));
      else setPwdError(typeof detail === "string" ? detail : t("profile.saveError"));
    },
  });

  const onPasswordSubmit = (e: FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    if (newPwd.length < 4) {
      setPwdError(t("profile.newPasswordTooShort"));
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdError(t("profile.passwordMismatch"));
      return;
    }
    pwdMutation.mutate();
  };

  if (profile.isLoading || !profile.data) return <CircularProgress />;

  const me = profile.data;

  return (
    <Box>
      <PageHeader title={t("profile.title")} subtitle={t("profile.subtitle")} helpTerm="profil" />

      <Grid container spacing={2}>
        {/* ---- Identity & Preferences --------------------------------- */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t("profile.identity")}
              </Typography>
              {identityError && <Alert severity="error" sx={{ mb: 2 }}>{identityError}</Alert>}
              {identitySaved && <Alert severity="success" sx={{ mb: 2 }}>{t("profile.saved")}</Alert>}
              <Stack spacing={2}>
                <TextField
                  label={t("profile.email")}
                  value={me.email}
                  disabled
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("profile.fullName")}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  fullWidth
                  size="small"
                />

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t("profile.preferences")}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                    <InputLabel>{t("profile.locale")}</InputLabel>
                    <Select
                      label={t("profile.locale")}
                      value={locale}
                      onChange={(e) => setLocale(e.target.value)}
                    >
                      <MenuItem value="fr">Français</MenuItem>
                      <MenuItem value="en">English</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                    <InputLabel>{t("profile.theme")}</InputLabel>
                    <Select
                      label={t("profile.theme")}
                      value={themePref}
                      onChange={(e) => setThemePref(e.target.value)}
                    >
                      <MenuItem value="light">{t("profile.themeLight")}</MenuItem>
                      <MenuItem value="dark">{t("profile.themeDark")}</MenuItem>
                      <MenuItem value="auto">{t("profile.themeAuto")}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                {me.practitioner && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t("profile.practitionerInfo")}
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label={t("practitioners.lastName")}
                        value={me.practitioner.display_name}
                        disabled
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label={t("profile.rpps")}
                        value={me.practitioner.rpps ?? ""}
                        disabled
                        size="small"
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                    <TextField
                      label={t("profile.phone")}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label={t("profile.bio")}
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder={t("profile.bioPlaceholder")}
                      multiline
                      minRows={3}
                      maxRows={8}
                      inputProps={{ maxLength: 2000 }}
                      fullWidth
                    />
                    <TextField
                      label={t("profile.languages")}
                      value={languagesInput}
                      onChange={(e) => {
                        setLanguagesInput(e.target.value);
                        setLanguages(
                          e.target.value
                            .split(/[,;\s]+/)
                            .map((s) => s.trim().toLowerCase())
                            .filter(Boolean),
                        );
                      }}
                      placeholder="fr, en, es"
                      helperText={t("profile.languagesHelp")}
                      size="small"
                      fullWidth
                    />
                    <PhotoUploader
                      practitionerId={me.practitioner.id}
                      hasPhoto={profile.data?.practitioner?.has_photo ?? false}
                      displayName={me.practitioner.display_name ?? "—"}
                      onChanged={() => qc.invalidateQueries({ queryKey: ["me-profile"] })}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={async () => {
                        try {
                          const blob = await downloadMyCv();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `cortex-cv-${me.practitioner?.last_name?.toLowerCase() ?? "practitioner"}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          alert((e as Error).message);
                        }
                      }}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {t("profile.downloadCv")}
                    </Button>
                  </>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    startIcon={<SaveIcon />}
                    variant="contained"
                    onClick={() => identityMutation.mutate()}
                    disabled={identityMutation.isPending}
                  >
                    {t("common.save")}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* ---- Security: password ------------------------------------ */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t("profile.security")}
              </Typography>
              {pwdError && <Alert severity="error" sx={{ mb: 2 }}>{pwdError}</Alert>}
              {pwdSaved && <Alert severity="success" sx={{ mb: 2 }}>{t("profile.passwordChanged")}</Alert>}
              <Box component="form" onSubmit={onPasswordSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label={t("profile.currentPassword")}
                    type="password"
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    autoComplete="current-password"
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("profile.newPassword")}
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    autoComplete="new-password"
                    fullWidth
                    size="small"
                    helperText="Min. 4 caractères"
                  />
                  <TextField
                    label={t("profile.confirmPassword")}
                    type="password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    autoComplete="new-password"
                    fullWidth
                    size="small"
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      type="submit"
                      startIcon={<LockResetIcon />}
                      variant="contained"
                      disabled={!currentPwd || !newPwd || !confirmPwd || pwdMutation.isPending}
                    >
                      {t("profile.changePassword")}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t("profile.roles")}
              </Typography>
              <Stack spacing={1}>
                {me.is_god && (
                  <Chip color="secondary" label="GOD" sx={{ alignSelf: "flex-start" }} />
                )}
                {(useAuthStore.getState().me?.roles ?? []).map((r, i) => (
                  <Chip
                    key={i}
                    variant="outlined"
                    label={`${r.role_code} · ${r.scope}`}
                    sx={{ alignSelf: "flex-start" }}
                  />
                ))}
                {!me.is_god && (useAuthStore.getState().me?.roles ?? []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {me.practitioner && (
          <Grid size={{ xs: 12 }}>
            <IcsSection />
          </Grid>
        )}

        {/* ---- Documents vault -------------------------------------- */}
        {profile.data?.practitioner && (
          <Grid size={{ xs: 12 }}>
            <DocumentsPanel />
          </Grid>
        )}

        {/* ---- Unavailability requests ------------------------------ */}
        {profile.data?.practitioner && (
          <Grid size={{ xs: 12 }}>
            <MyUnavailabilitiesPanel />
          </Grid>
        )}

        {/* ---- Security: MFA ----------------------------------------- */}
        <Grid size={{ xs: 12 }}>
          <MfaPanel />
        </Grid>

        {/* ---- Security: SSO identities ------------------------------ */}
        <Grid size={{ xs: 12 }}>
          <SsoLinkPanel />
        </Grid>

        {/* ---- Security: active sessions ----------------------------- */}
        <Grid size={{ xs: 12 }}>
          <SessionsPanel />
        </Grid>

        {/* ---- Push notifications (per device) ------------------------ */}
        <Grid size={{ xs: 12 }}>
          <PushNotificationsPanel />
        </Grid>

        {/* ---- Notification preferences ------------------------------ */}
        <Grid size={{ xs: 12 }}>
          <NotificationPreferencesPanel />
        </Grid>

        {/* ---- Coordinator backup designations ----------------------- */}
        <Grid size={{ xs: 12 }}>
          <CoordinatorBackupPanel />
        </Grid>
      </Grid>
    </Box>
  );
}
